import { LookTable } from "@/core/types"
import { Adjustments } from "@/core/types"
import {
	InputColorMatchFeaturesWithFaceData,
	ReferenceColorMatchFeatures,
} from "@/lib/ai/color-match/utils"
import { LRU } from "@/utils/lru-cache"
import { AI_WORKER_REF_BITMAP_CACHE_MAX_SIZE } from "@/lib/ai/utils"

export class ColorMatchCache {
	private referenceFeaturesCache = new LRU<string, ReferenceColorMatchFeatures>(1000)
	private inputFeaturesCache = new LRU<string, InputColorMatchFeaturesWithFaceData>(1000)

	private aiWorkerReferenceBitmapCache = new Set<string>()

	private adjustmentsCache = new LRU<string, Adjustments>(1000)
	private skinToneOptimizedAdjustmentsCache = new LRU<string, Adjustments>(1000)
	private lutsCache = new LRU<string, LookTable>(100)
	private lutAdjustmentsCache = new LRU<string, Adjustments>(100)

	constructor() {}

	// cache reference features
	setReferenceFeatures(referenceId: string, features: ReferenceColorMatchFeatures): void {
		this.referenceFeaturesCache.set(referenceId, features)
	}

	getReferenceFeatures(referenceId: string): ReferenceColorMatchFeatures | undefined {
		return this.referenceFeaturesCache.get(referenceId)
	}

	// sync with reference bitmap cache in AI worker
	setAiWorkerReferenceBitmap(referenceId: string): void {
		// If we're at capacity, clear everything
		if (
			!this.aiWorkerReferenceBitmapCache.has(referenceId) &&
			this.aiWorkerReferenceBitmapCache.size >= AI_WORKER_REF_BITMAP_CACHE_MAX_SIZE
		) {
			this.aiWorkerReferenceBitmapCache.clear()
		}
		this.aiWorkerReferenceBitmapCache.add(referenceId)
	}

	removeAiWorkerReferenceBitmap(referenceId: string): void {
		this.aiWorkerReferenceBitmapCache.delete(referenceId)
	}

	hasReferenceBitmapInAIWorker(referenceId: string): boolean {
		return this.aiWorkerReferenceBitmapCache.has(referenceId)
	}

	// cache input features
	setInputFeatures(imageId: string, features: InputColorMatchFeaturesWithFaceData): void {
		this.inputFeaturesCache.set(imageId, features)
	}

	getInputFeatures(imageId: string): InputColorMatchFeaturesWithFaceData | undefined {
		return this.inputFeaturesCache.get(imageId)
	}

	// cache computed adjustments and luts
	setAdjustments(colorMatchId: string, adjustments: Adjustments): void {
		this.adjustmentsCache.set(colorMatchId, adjustments)
	}

	getAdjustments(colorMatchId: string): Adjustments | undefined {
		return this.adjustmentsCache.get(colorMatchId)
	}

	setSkinToneOptimizedAdjustments(colorMatchId: string, adjustments: Adjustments): void {
		this.skinToneOptimizedAdjustmentsCache.set(colorMatchId, adjustments)
	}

	getSkinToneOptimizedAdjustments(colorMatchId: string): Adjustments | undefined {
		return this.skinToneOptimizedAdjustmentsCache.get(colorMatchId)
	}

	setLut(colorMatchId: string, lut: LookTable): void {
		this.lutsCache.set(colorMatchId, lut)
	}

	getLut(colorMatchId: string): LookTable | undefined {
		return this.lutsCache.get(colorMatchId)
	}

	setLutAdjustments(colorMatchId: string, adjustments: Adjustments): void {
		this.lutAdjustmentsCache.set(colorMatchId, adjustments)
	}

	getLutAdjustments(colorMatchId: string): Adjustments | undefined {
		return this.lutAdjustmentsCache.get(colorMatchId)
	}
}

export const colorMatchCache = new ColorMatchCache()
